import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { workspaceSlug } from "../lib/hosts";
import { useState } from "react";
import { Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { useWorkspace } from "../lib/workspaces";
import { useArchivedProjects, useJoinProject, useProjects } from "../lib/projects";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CreateProjectForm } from "../components/projects/CreateProjectForm";
import { ProjectListEmptyState } from "../components/projects/ProjectListEmptyState";
import { strings } from "../lib/strings";
import { contributes } from "../../shared/schemas/workspaces";
import { ApiError } from "../lib/api";
import type { PublicProject } from "../../shared/types/projects";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { QueryGate, Unavailable } from "../components/ui/states";

function OrgHomeIndex() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const projects = useProjects(slug);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const archived = useArchivedProjects(slug, showArchived);
  const join = useJoinProject(slug);

  if (view.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 py-12">
        <div className="h-8 w-72 animate-pulse rounded bg-[hsl(var(--muted))]" />
        <div className="h-4 w-96 animate-pulse rounded bg-[hsl(var(--muted))]" />
      </div>
    );
  }
  if (view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 401)) {
    return <ToWorkspaces />;
  }
  if (!view.data) return <ToWorkspaces />;
  // A Viewer creates no project (BUILD_SPEC §8.2); the API refuses them as well.
  const contributor = contributes(view.data.role);

  const list = projects.data ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-8 py-12">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{view.data.workspace.name}</h1>
        </div>
        {view.data.role === "administrator" && (
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/settings">Workspace settings</Link>
          </Button>
        )}
      </header>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{strings.projects.list.title}</h2>
          {list.length > 0 && contributor && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {strings.projects.list.createButton}
            </Button>
          )}
        </div>

        {projects.data === undefined && projects.isError ? (
          <Unavailable what={strings.projects.list.theProjects} onRetry={() => void projects.refetch()} />
        ) : projects.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded bg-[hsl(var(--muted))]" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <ProjectListEmptyState onCreate={contributor ? () => setCreateOpen(true) : undefined} />
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {list.map((p) =>
              p.joinable ? (
                <Card key={p.id}>
                  <CardHeader>
                    <ProjectTitle project={p} />
                    <CardDescription>{strings.projects.list.joinHint}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={join.isPending}
                      onClick={() =>
                        join.mutate(p.id, {
                          onSuccess: () => toast.success(strings.projects.list.joined.replace("{name}", p.name)),
                          onError: () => toast.error(strings.projects.list.joinFailed),
                        })
                      }
                    >
                      {strings.projects.list.join}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <ProjectLink key={p.id} project={p} />
              ),
            )}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <Button variant="ghost" size="sm" aria-expanded={showArchived} onClick={() => setShowArchived(!showArchived)}>
          {strings.projects.list.archived}
        </Button>
        {showArchived && (
          <QueryGate
            query={archived}
            what={strings.projects.list.theArchived}
            skeleton={
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded bg-[hsl(var(--muted))]" />
                ))}
              </div>
            }
          >
            {(list) =>
              list.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{strings.projects.list.noneArchived}</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {list.map((p) => (
                    <ProjectLink key={p.id} project={p} />
                  ))}
                </div>
              )
            }
          </QueryGate>
        )}
      </section>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{strings.projects.create.title}</DialogTitle>
            <DialogDescription>{strings.projects.create.subtitle}</DialogDescription>
          </DialogHeader>
          <CreateProjectForm
            slug={slug}
            onCancel={() => setCreateOpen(false)}
            onCreated={(p) => {
              setCreateOpen(false);
              void navigate({ to: "/admin/p/$projectKey", params: { projectKey: p.key } });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** A project's key, name and whether it is private or archived. */
function ProjectTitle({ project }: { project: PublicProject }) {
  return (
    <CardTitle className="flex items-center gap-2">
      <span className="rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 font-mono text-xs text-[hsl(var(--muted-foreground))]">
        {project.key}
      </span>
      <span>{project.name}</span>
      {project.visibility === "private" && (
        <span className="inline-flex items-center gap-1 text-xs font-normal text-[hsl(var(--muted-foreground))]">
          <Lock className="h-3 w-3" aria-hidden />
          {strings.projects.list.private}
        </span>
      )}
      {project.archivedAt !== null && (
        <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">
          {strings.projects.list.archivedBadge}
        </span>
      )}
    </CardTitle>
  );
}

function ProjectLink({ project }: { project: PublicProject }) {
  return (
    <Link
      to="/admin/p/$projectKey"
      params={{ projectKey: project.key }}
      className="block rounded-lg focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none"
    >
      <Card className="transition-colors hover:bg-[hsl(var(--accent))]">
        <CardHeader>
          <ProjectTitle project={project} />
          {project.description && <CardDescription>{project.description}</CardDescription>}
        </CardHeader>
        <CardContent />
      </Card>
    </Link>
  );
}

export const Route = createFileRoute("/admin/")({ component: OrgHomeIndex });
